import { DotsThreeVertical, Plus } from "@phosphor-icons/react"
import { Circle } from "@phosphor-icons/react/dist/ssr"
import { Button, Menu, MenuItem, Popover } from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useSearchParams } from "react-router-dom"
import { ReactComponent as EmptyListing } from "~/assets/EmptyListing.svg"
import { AddSetutradeProjectionDetails } from "~/components/CreditManagement/Assessment/AddSetutradeProjectionDetails"
import { useTradeAssessmentDynamicQuery } from "~/generated/graphql"

export type TransactionAssessmentType = {
  form?: any
  isDisabled?: boolean
  isSubmitted?: boolean
}

export const TransactionAssessment = ({
  form,
  isDisabled = false,
  isSubmitted = false,
}: TransactionAssessmentType) => {
  const [searchParams, setSearchParams] = useSearchParams()

  function inputProps(key) {
    return {
      ...form.getInputProps(key),
      disabled: isDisabled,
    }
  }

  const getCols = ({
    entity = "setutrade",
  }: {
    entity?: "setutrade" | "projection"
  }) => {
    return [
      {
        Header: "tenure",
        accessor: "tenure",
        id: "tenure",
        Cell({ value }) {
          return (
            <Cell
              title={value?.name}
              subtitle={value?.days + " days"}
              subtitleClassName="text-brown-400 !font-400"
            />
          )
        },
      },

      {
        Header: "Payment Cycle",
        accessor: "payment_cycle",
        id: "payment_cycle",
        Cell({ value }) {
          return <Cell title={value + " days"} />
        },
      },

      {
        Header: "Start Date",
        accessor: "start_date",
        Cell({ value }) {
          return <Cell title={dayjs(value).format("D MMM 'YY")} />
        },
      },

      {
        Header: "End Date",
        accessor: "end_date",
        Cell({ value }) {
          return <Cell title={dayjs(value).format("D MMM 'YY")} />
        },
      },
      entity === "setutrade" && {
        Header: "No. of Txns",
        accessor: "trade_count",
        headerClassName: "!hidden",
        Cell({ value }) {
          const txns = assessmentData?.trade_assessment_dynamic?.trade_count
          return <Cell title={value || txns || "--"} />
        },
      },
      entity === "setutrade" && {
        Header: "No. of settled",
        accessor: "settled_trade_count",
        Cell({ value }) {
          const settled =
            assessmentData?.trade_assessment_dynamic?.settled_trade_count
          return <Cell title={value || settled || "--"} />
        },
      },
      entity === "setutrade" && {
        Header: "No. of Unsettled",
        accessor: "unsettled_trade_count",
        Cell({ value }) {
          const unsettled =
            assessmentData?.trade_assessment_dynamic?.unsettled_trade_count
          return <Cell title={value || unsettled || "--"} />
        },
      },
      {
        Header: "Total Sales",
        accessor: "total_sales",
        Cell({ value }) {
          if (!value) return "--"
          return <Cell title={inr(value)} titleClassName="!text-green-400" />
        },
      },
      {
        Header: "Unit daySale",
        accessor: "daily_average_sale",
        id: "unit_sale",
        Cell({ row: { original } }) {
          const noOfDays = original?.tenure?.days
          const total_sales = Number(original?.total_sales)
          return (
            <Cell
              title={inr(Number(Number(total_sales / noOfDays).toFixed(2)))}
              titleClassName="!text-green-400"
            />
          )
        },
      },
      {
        Header: "Assessed Limit",
        accessor: "assessed_limit",
        id: "assessed",
        Cell({ value, row: { original } }) {
          const paymentCycle = Number(original?.payment_cycle)
          const totalSale = original?.total_sales
          const noOfDays = original?.tenure?.days
          const unitDaySale = Number(Number(totalSale / noOfDays).toFixed(2))

          return (
            <Cell
              title={inr(unitDaySale * paymentCycle)}
              titleClassName="!text-green"
            />
          )
        },
      },
      {
        Header: "",
        accessor: "action",
        id: "action",
        Cell({ row: { original } }) {
          const id = original?.id || original?._id

          return (
            <Popover
              minimal
              arrow={false}
              className="p-0"
              trigger="click"
              interactive
              placement="top"
              content={
                <Menu className="max-h-72">
                  <MenuItem
                    onClick={() => {
                      searchParams.set(entity, id)
                      setSearchParams(searchParams)
                    }}
                    className="!font-400"
                  >
                    Edit
                  </MenuItem>
                </Menu>
              }
            >
              <button className="hover:bg-gray-300 rounded" hidden={isDisabled}>
                <DotsThreeVertical size={20} />
              </button>
            </Popover>
          )
        },
      },
    ]?.filter(a => a)
  }

  const tradeAssessment = form?.values?.trade_assessments?.[0]
  const [{ data: assessmentData }] = useTradeAssessmentDynamicQuery({
    variables: {
      input: {
        end_date: dayjs(tradeAssessment?.end_date).format("YYYY-MM-DD"),
        payment_cycle: Number(tradeAssessment?.payment_cycle),
        start_date: dayjs(tradeAssessment?.start_date).format("YYYY-MM-DD"),
        tenure: tradeAssessment?.tenure?.days,
        user_id: form?.values?.user?.shared_id,
      },
    },
    pause: !form?.values?.user?.shared_id || !tradeAssessment?.end_date,
  })

  return (
    <>
      <header className="text-lg text-green font-500 bg-gray-200 p-3 border-y-1 border-gray-300">
        Transaction Assessment
      </header>

      <section id="credit_limit" hidden={form?.values?.isNew}>
        <header className="border-b-1 border-gray-300 p-3 flex justify-between">
          <div className="flex gap-1 items-center text-green ">
            <Circle weight="fill" className="text-green" />
            Setutrade Assessment
          </div>
          <div>
            <Button
              size="sm"
              text="SetuTrade Details"
              leftIcon={<Plus />}
              variant="outline"
              disabled={isDisabled}
              onClick={() => {
                searchParams.set("setutrade", "true")
                setSearchParams(searchParams)
              }}
              className={classNames(
                !form?.values?.trade_assessments?.length ? "" : "hidden"
              )}
              intent={
                isSubmitted && !form?.values?.trade_assessments?.length
                  ? "danger"
                  : "primary"
              }
            />
          </div>
        </header>
        <div className="py-3  border-b-1 border-gray-300 ">
          <TableView
            data={form?.values?.trade_assessments || []}
            tableClassName="!m-0 !shadow-none"
            columns={getCols({})}
            noDataProps={{
              image: <EmptyListing />,
              title: "Empty Listing",
              className: "min-h-[10rem] !p-0",
            }}
            getCellProps={cell => {
              if (
                cell?.column?.id === "payment_cycle" ||
                cell?.column?.id === "tenor"
              ) {
                return {
                  className: "font-700 !text-green-800",
                }
              } else {
                return {
                  className: "font-700 !text-green-800",
                }
              }
            }}
            getHeaderProps={h => {
              return { className: "!z-0" }
            }}
          />
        </div>
      </section>

      <section id="credit_limit">
        <header className="border-b-1 border-gray-300 p-3 flex justify-between">
          <div className="flex gap-1 items-center text-green ">
            <Circle weight="fill" className="text-green" />
            Projection Assessment
          </div>
          <div>
            <Button
              size="sm"
              text="Projection Details"
              leftIcon={<Plus />}
              disabled={isDisabled}
              variant="outline"
              className={classNames(
                !form?.values?.projection_assessments?.length ? "" : "hidden"
              )}
              onClick={() => {
                searchParams.set("projection", "true")
                setSearchParams(searchParams)
              }}
              intent={
                isSubmitted && !form?.values?.projection_assessments?.length
                  ? "danger"
                  : "primary"
              }
            />
          </div>
        </header>
        <div className="py-3  border-b-1 border-gray-300 ">
          <TableView
            data={form?.values?.projection_assessments || []}
            tableClassName="!m-0 !shadow-none"
            columns={getCols({ entity: "projection" })}
            noDataProps={{
              image: <EmptyListing />,
              title: "Empty Listing",
              className: "min-h-[10rem] !p-0",
            }}
            getCellProps={cell => {
              if (
                cell?.column?.id !== "action" &&
                cell?.column?.id !== "unit_sale" &&
                cell?.column?.id !== "assessed"
              ) {
                return {
                  className: "font-700 !text-green-400",
                }
              } else {
                return { className: "font-700" }
              }
            }}
            getHeaderProps={h => {
              return { className: "!z-0" }
            }}
          />
        </div>
      </section>

      <AddSetutradeProjectionDetails form={form} isDisabled={isDisabled} />
    </>
  )
}
